// Header market clocks: featured exchange chips + an "All markets" panel.
// Pure client-side (src/markets/sessions.js) — no market data feed involved.

import { EXCHANGES, exchangeStatus, countdown } from '../markets/sessions.js';
import { t } from '../i18n/index.js';

const KIND_LABEL = new Proxy({}, { get: (_, k) => t(`mc.${k}`) });

const UNTIL_KEY = { open: 'mc.closesIn', pre: 'mc.opensIn', post: 'mc.endsIn', night: 'mc.endsIn', break: 'mc.reopensIn' };
const NEXT_KEY = { open: 'mc.opensIn', pre: 'mc.preIn', post: 'mc.afterIn', night: 'mc.nightIn' };

function subline(st, now) {
    if (st.until) return t(UNTIL_KEY[st.kind], { t: countdown(st.until - now) });
    if (st.next) return t(NEXT_KEY[st.next.kind] || 'mc.opensIn', { t: countdown(st.next.at - now) });
    return st.kind === 'open' ? '24/7' : '';
}

const esc = (s) =>
    String(s).replace(
        /[&<>"']/g,
        (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
    );

export function initMarketClocks(root = document.getElementById('market-clocks')) {
    if (!root) return;
    const featured = EXCHANGES.filter((e) => e.featured);
    let open = false;

    function render() {
        const now = Date.now();
        const states = new Map(EXCHANGES.map((ex) => [ex.id, exchangeStatus(ex, now)]));
        const openCount = EXCHANGES.filter(
            (ex) => !ex.always && ['open'].includes(states.get(ex.id).kind),
        ).length;

        const chips = featured
            .map((ex, i) => {
                const st = states.get(ex.id);
                return `<button type="button" class="mc-chip mc-${st.kind}" data-rank="${i + 1}" data-mc-toggle
                    title="${esc(`${ex.name} · ${KIND_LABEL[st.kind]} · ${t('mc.local')} ${st.localTime}`)}">
                    <span class="mc-top"><span class="mc-dot"></span><b>${esc(ex.short)}</b><span class="mc-state">${esc(KIND_LABEL[st.kind])}</span></span>
                    <span class="mc-sub">${esc(subline(st, now))}</span>
                </button>`;
            })
            .join('');

        const rows = EXCHANGES.map((ex) => {
            const st = states.get(ex.id);
            return `<div class="mc-row mc-${st.kind}">
                <span class="mc-row-name"><span class="mc-dot"></span>${esc(ex.name)}</span>
                <span class="mc-row-time">${esc(ex.always ? '24/7' : `${ex.city} ${st.localTime}`)}</span>
                <span class="mc-row-sub"><span class="mc-state">${esc(KIND_LABEL[st.kind])}</span>${subline(st, now) ? ' · ' + esc(subline(st, now)) : ''}${ex.holidaysKnown ? '' : ' · ' + esc(t('mc.noHolidays'))}</span>
            </div>`;
        }).join('');

        root.innerHTML = `${chips}
            <button type="button" class="mc-chip mc-all" data-mc-toggle aria-expanded="${open}" aria-controls="mc-panel">
                <svg class="mc-all-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                <span class="mc-all-n">${esc(t('mc.openCount', { n: openCount }))}</span><span class="mc-all-label"> &middot; ${esc(t('mc.all'))}</span> ${open ? '&#9652;' : '&#9662;'}
            </button>
            <div class="mc-panel" id="mc-panel" role="dialog" aria-label="Market hours" ${open ? '' : 'hidden'}>
                ${rows}
                <p class="mc-note">${esc(t('mc.note'))}</p>
            </div>`;
    }

    root.addEventListener('click', (e) => {
        // Clicks inside the clocks never count as "outside": render() replaces
        // the clicked button, so the document handler would see a detached
        // target and close the panel in the same tick it opened.
        e.stopPropagation();
        if (e.target.closest('[data-mc-toggle]')) {
            open = !open;
            render();
        }
    });
    document.addEventListener('click', (e) => {
        if (open && !root.contains(e.target)) {
            open = false;
            render();
        }
    });
    document.addEventListener('keydown', (e) => {
        if (open && e.key === 'Escape') {
            open = false;
            render();
        }
    });

    render();
    // Re-render on the minute boundary, then every 30s (countdowns are minute-resolution)
    setTimeout(
        () => {
            render();
            setInterval(render, 30000);
        },
        60000 - (Date.now() % 60000),
    );
}
